import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  TextField,
  CircularProgress,
  InputLabel
} from "@mui/material";
import MDSelect from "components/MDSelect";
import CampanhaCreateEdit from "./CampanhaCreateEdit";
import { get, post, postNoResponse } from "utils/WebService";
import { infoAviso, successAlert, warningAlert } from "utils/Functions";
import { useMaterialUIController } from "context";
import { ESCURO } from "assets/coresTema";
import { ehCarteiraDeCobranca } from "utils/Carteiras";

// Filtro de cliente: em branco = todas; SEM_CLIENTE = campanhas sem cliente, que valem para todos (CampanhaPageable)
const SEM_CLIENTE = "SEM_CLIENTE";
// Filtro de carteira: em branco = todas; SEM_CARTEIRA = campanhas sem carteira, que valem para todas (CampanhaPageable)
const SEM_CARTEIRA = "SEM_CARTEIRA";
// Situação pela vigência e pelo cadastro (CampanhaPageable.situacao), sem gravar nada: campanha vencida já não vale
// (as fichas voltam para a campanha sem marcação/padrão do cliente), mas continua "ativa" no cadastro
const OPCOES_STATUS = [
  { value: "", label: "Todas" },
  { value: "ATIVAS", label: "Ativas (valendo hoje)" },
  { value: "A_COMECAR", label: "A começar" },
  { value: "ENCERRADAS", label: "Encerradas" },
  { value: "INATIVAS", label: "Inativas" },
];

// Dia (aaaammdd) de uma data da API (número/epoch ou texto), no fuso do navegador
const diaDe = (valor) => {
  if (valor === null || valor === undefined || valor === "") return null;
  const d = new Date(valor);
  return isNaN(d.getTime()) ? null : d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
};
const dataBr = (valor) => new Date(valor).toLocaleDateString("pt-BR");

// Situação mostrada na coluna Status
const situacaoDaCampanha = (item) => {
  if (!item.cbAtivo) return { texto: "Inativo", cor: "error", dica: "Desligada no cadastro: não vale para nenhuma ficha." };
  const hoje = diaDe(new Date());
  const fim = diaDe(item.cdFim);
  const inicio = diaDe(item.cdInicio);
  if (fim !== null && fim < hoje) {
    return {
      texto: `Encerrada em ${dataBr(item.cdFim)}`,
      cor: "warning",
      dica: "A vigência acabou: a campanha não vale mais, e as fichas voltam para a campanha sem marcação (a padrão) do cliente, mesmo com a marcação. Prorrogando a vigência, ela volta a valer.",
    };
  }
  if (inicio !== null && inicio > hoje) {
    return { texto: `Começa em ${dataBr(item.cdInicio)}`, cor: "info", dica: "Ainda não vale: começa no início da vigência." };
  }
  return { texto: "Ativo", cor: "success", dica: "Valendo hoje." };
};

export default function CampanhaConsult() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [openModal, setOpenModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Estados para Duplicação
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [itemToDuplicate, setItemToDuplicate] = useState(null);
  const [duplicateNome, setDuplicateNome] = useState("");
  const [duplicateClienteId, setDuplicateClienteId] = useState("");
  const [duplicateSubCarteiraId, setDuplicateSubCarteiraId] = useState("");
  const [clientes, setClientes] = useState([]);
  const [subCarteiras, setSubCarteiras] = useState([]);
  const [duplicating, setDuplicating] = useState(false);

  // Filtros da lista (aplicados ao escolher)
  const [filtroCliente, setFiltroCliente] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState(OPCOES_STATUS[0]);
  const [filtroCarteira, setFiltroCarteira] = useState(null);
  const [carteiras, setCarteiras] = useState([]);

  const opcoesCarteira = [
    { value: "", label: "Todas" },
    { value: SEM_CARTEIRA, label: "Sem carteira (vale para todas)" },
    ...carteiras.map((c) => ({ value: String(c.id), label: c.nome || c.ccNome || "" })),
  ];

  const opcoesCliente = [
    { value: "", label: "Todos" },
    { value: SEM_CLIENTE, label: "Sem cliente (vale para todos)" },
    ...clientes.map((c) => ({ value: c.id, label: c.nomeCompleto || c.nomeComum || c.ccNome || "" })),
  ];

  const nomeDoCliente = (item) => {
    if (!item.cliente?.id) return "Todos os clientes";
    const c = clientes.find((x) => String(x.id) === String(item.cliente.id));
    return c ? (c.nomeCompleto || c.nomeComum || c.ccNome) : (item.cliente.nomeCompleto || "-");
  };

  const fetchData = (cliente = filtroCliente, status = filtroStatus, carteira = filtroCarteira) => {
    setLoading(true);
    post("/campanha/list", {
      page: 0,
      pageSize: 500,
      sorted: [],
      filtered: [],
      clienteId: cliente?.value || null,
      situacao: status?.value || null,
      carteiraId: carteira?.value || null,
    }).then(({ data }) => {
      setDataList(data || []);
    }).catch((e) => {
      console.error(e);
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
    get("/pessoa/list-all").then((res) => {
      setClientes(res.list || res.data || (Array.isArray(res) ? res : []));
    });
    get("/carteira/list-all").then((res) => setCarteiras((res?.list || []).filter(ehCarteiraDeCobranca))).catch(() => setCarteiras([]));
  }, []);

  const fetchSubCarteiras = (clienteId) => {
    if (!clienteId) {
      setSubCarteiras([]);
      return;
    }
    get(`/pessoa/by-id/${clienteId}`).then((res) => {
      setSubCarteiras(res.subCarteiras || []);
    });
  };

  const handleEdit = (id) => {
    setSelectedId(id);
    setOpenModal(true);
  };

  const handleCreate = () => {
    setSelectedId(null);
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    fetchData();
  };

  const mudarCliente = (opcao) => {
    const valor = opcao?.value ? opcao : null;
    setFiltroCliente(valor);
    fetchData(valor, filtroStatus, filtroCarteira);
  };

  const mudarCarteira = (opcao) => {
    const valor = opcao?.value ? opcao : null;
    setFiltroCarteira(valor);
    fetchData(filtroCliente, filtroStatus, valor);
  };

  const mudarStatus = (opcao) => {
    const valor = opcao || OPCOES_STATUS[0];
    setFiltroStatus(valor);
    fetchData(filtroCliente, valor, filtroCarteira);
  };

  const limparFiltros = () => {
    setFiltroCliente(null);
    setFiltroStatus(OPCOES_STATUS[0]);
    setFiltroCarteira(null);
    fetchData(null, OPCOES_STATUS[0], null);
  };

  const temFiltro = !!filtroCliente || !!filtroCarteira || filtroStatus.value !== "";

  const handleDelete = (id) => {
    infoAviso("Deseja realmente excluir esta campanha?", async () => {
      postNoResponse(`/campanha/delete/${id}`).then(() => {
        successAlert("Campanha removida com sucesso!");
        fetchData();
      }).catch((e) => {
        warningAlert("Erro ao excluir campanha.");
      });
    }, () => null);
  };

  const handleDuplicate = (item) => {
    setItemToDuplicate(item);
    setDuplicateNome(`${item.ccNome || ""} (Cópia)`);
    const cliId = item.cliente?.id || "";
    setDuplicateClienteId(cliId);
    const subcId = item.subCarteira?.id || "";
    setDuplicateSubCarteiraId(subcId);
    fetchSubCarteiras(cliId);
    setDuplicateModalOpen(true);
  };

  const confirmDuplicate = () => {
    if (!duplicateNome || !duplicateNome.trim()) {
      warningAlert("Por favor, informe o nome da nova campanha.");
      return;
    }
    setDuplicating(true);
    postNoResponse("/campanha/duplicate", {
      origemId: itemToDuplicate.id,
      novoNome: duplicateNome.trim(),
      novoClienteId: duplicateClienteId || null,
      novaSubCarteiraId: duplicateSubCarteiraId || null,
    }).then(() => {
      successAlert("Campanha duplicada com sucesso!");
      setDuplicateModalOpen(false);
      setItemToDuplicate(null);
      fetchData();
    }).catch((e) => {
      console.error(e);
      warningAlert("Erro ao duplicar campanha.");
    }).finally(() => {
      setDuplicating(false);
    });
  };

  const getTipoLabel = (tipo) => {
    switch (Number(tipo)) {
      case 1: return <MDBadge badgeContent="Global (Devedor)" color="info" variant="gradient" size="sm" />;
      case 2: return <MDBadge badgeContent="Por Faixa de Atraso" color="warning" variant="gradient" size="sm" />;
      case 3: return <MDBadge badgeContent="Marcação Especial" color="secondary" variant="gradient" size="sm" />;
      case 4: return <MDBadge badgeContent="Por Ano de Vencimento" color="success" variant="gradient" size="sm" />;
      default: return "-";
    }
  };

  return (
    <MDBox pt={1} pb={3}>
      <Card
        variant="outlined"
        sx={{
          px: 2,
          py: 1.5,
          mb: 2,
          background: darkMode ? ESCURO.cartao : "#fafafa",
          borderColor: darkMode ? ESCURO.divisoria : "#ddd",
        }}
      >
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cliente</InputLabel>
            <MDSelect
              placeholder="Todos"
              options={opcoesCliente}
              getOptionLabel={(o) => o.label}
              value={filtroCliente}
              onChange={mudarCliente}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Carteira</InputLabel>
            <MDSelect
              placeholder="Todas"
              options={opcoesCarteira}
              getOptionLabel={(o) => o.label}
              value={filtroCarteira}
              onChange={mudarCarteira}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
            <MDSelect
              options={OPCOES_STATUS}
              getOptionLabel={(o) => o.label}
              value={filtroStatus}
              onChange={mudarStatus}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>&nbsp;</InputLabel>
            <MDButton variant="contained" color="info" size="medium" fullWidth sx={{ height: 38 }}
              onClick={limparFiltros} disabled={!temFiltro}>
              Limpar filtro
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant="caption" color="text" sx={{ px: 1 }}>
              {loading ? "Carregando..." : `${dataList.length} campanha(s)`}
              {!loading && filtroCliente?.value && filtroCliente.value !== SEM_CLIENTE
                ? ". As campanhas sem cliente também valem para este cliente: escolha \"Sem cliente\" para vê-las."
                : ""}
              {!loading && filtroCarteira?.value && filtroCarteira.value !== SEM_CARTEIRA
                ? " As campanhas sem carteira também valem para esta carteira (a da carteira vem antes)."
                : ""}
            </MDTypography>
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ background: darkMode ? ESCURO.cartao : "#fafafa" }}>
        <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>
            Gestão de Campanhas e Regras
          </MDTypography>
          <MDButton variant="gradient" color="info" onClick={handleCreate} startIcon={<Icon>add</Icon>} size="small">
            Nova Campanha
          </MDButton>
        </MDBox>

        <MDBox p={3}>
          <TableContainer sx={{ boxShadow: "none" }}>
            <Table>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell align="left"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Nome da Campanha</MDTypography></TableCell>
                  <TableCell align="left"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Cliente</MDTypography></TableCell>
                  <TableCell align="left"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Carteira</MDTypography></TableCell>
                  <TableCell align="center"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Tipo</MDTypography></TableCell>
                  <TableCell align="center"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Vigência</MDTypography></TableCell>
                  <TableCell align="center"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Status</MDTypography></TableCell>
                  <TableCell align="center"><MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>Ações</MDTypography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center"><MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>Carregando...</MDTypography></TableCell>
                  </TableRow>
                ) : dataList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center"><MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>{temFiltro ? "Nenhuma campanha com esses filtros." : "Nenhuma campanha cadastrada."}</MDTypography></TableCell>
                  </TableRow>
                ) : dataList.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <MDBox display="flex" alignItems="center">
                        <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>
                          {item.ccNome}
                        </MDTypography>
                        {item.cbPadrao && (
                          <MDBox ml={1}>
                            <MDBadge badgeContent="Padrão" color="primary" variant="contained" size="sm" />
                          </MDBox>
                        )}
                        {item.nomeMarcacaoEspecial && (
                          <Tooltip title="Vale só para a ficha ou o contrato com esta marcação">
                            <MDBox ml={1}>
                              <MDBadge badgeContent={`Marcação: ${item.nomeMarcacaoEspecial}`} color="secondary" variant="contained" size="sm" />
                            </MDBox>
                          </Tooltip>
                        )}
                      </MDBox>
                    </TableCell>
                    <TableCell>
                      <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"} title={nomeDoCliente(item)}
                        sx={{ display: "block", maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {nomeDoCliente(item)}
                      </MDTypography>
                    </TableCell>
                    <TableCell>
                      <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>
                        {item.nomeCarteira || "Todas as carteiras"}
                      </MDTypography>
                    </TableCell>
                    <TableCell align="center">{getTipoLabel(item.cvTipoAplicacao)}</TableCell>
                    <TableCell align="center">
                      <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>
                        {item.cdInicio
                          ? `${dataBr(item.cdInicio)} até ${item.cdFim ? dataBr(item.cdFim) : "Indefinida"}`
                          : (item.cdFim ? `Até ${dataBr(item.cdFim)}` : "Indefinida")}
                      </MDTypography>
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const sit = situacaoDaCampanha(item);
                        return (
                          <Tooltip title={sit.dica}>
                            <MDTypography variant="button" fontWeight="regular" color={sit.cor} sx={{ whiteSpace: "nowrap" }}>
                              {sit.texto}
                            </MDTypography>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Duplicar Campanha">
                        <IconButton color="secondary" onClick={() => handleDuplicate(item)}>
                          <Icon fontSize="small">content_copy</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton color="info" onClick={() => handleEdit(item.id)}>
                          <Icon fontSize="small">edit</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton color="error" onClick={() => handleDelete(item.id)}>
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </MDBox>
      </Card>

      {/* Modal de Duplicação de Campanha */}
      <Dialog
        open={duplicateModalOpen}
        onClose={() => !duplicating && setDuplicateModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? ESCURO.painel : "#ffffff",
            color: darkMode ? "#ffffff" : "inherit"
          }
        }}
      >
        <DialogTitle sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
          Duplicar Campanha
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: darkMode ? ESCURO.divisoria : "#e0e0e0" }}>
          <MDBox mb={2}>
            <MDTypography variant="caption" color={darkMode ? "white" : "text"} display="block">
              Campanha de Origem: <strong>{itemToDuplicate?.ccNome}</strong>
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : "text.secondary" }} display="block">
              Todas as regras, prazos, limites e tabelas de parcelamento serão copiados para a nova campanha.
            </MDTypography>
          </MDBox>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                Nome da Nova Campanha *
              </MDTypography>
              <MDInput
                fullWidth
                size="small"
                value={duplicateNome}
                onChange={(e) => setDuplicateNome(e.target.value)}
                placeholder="Ex: Campanha Banco X (Nova)"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                Cliente
              </MDTypography>
              <Autocomplete
                options={clientes}
                getOptionLabel={(option) => (option ? (option.nomeCompleto || option.ccNome || "") : "")}
                value={clientes.find(c => String(c.id) === String(duplicateClienteId)) || null}
                onChange={(event, newValue) => {
                  const newId = newValue ? newValue.id : "";
                  setDuplicateClienteId(newId);
                  fetchSubCarteiras(newId);
                  setDuplicateSubCarteiraId("");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Selecione o Cliente (opcional - Todos)"
                    fullWidth
                   
                  />
                )}
                noOptionsText="Nenhum cliente encontrado"
              />
            </Grid>

            {subCarteiras && subCarteiras.length > 0 && (
              <Grid item xs={12}>
                <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"} display="block" mb={0.5}>
                  Sub Carteira
                </MDTypography>
                <Autocomplete
                  options={subCarteiras}
                  getOptionLabel={(option) => (option ? (option.ccNome || "") : "")}
                  value={subCarteiras.find(s => String(s.id) === String(duplicateSubCarteiraId)) || null}
                  onChange={(event, newValue) => {
                    setDuplicateSubCarteiraId(newValue ? newValue.id : "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Selecione a Sub Carteira (opcional - Todas)"
                      fullWidth
                     
                    />
                  )}
                  noOptionsText="Nenhuma sub carteira encontrada"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setDuplicateModalOpen(false)}
            disabled={duplicating}
            sx={{
              color: darkMode ? "#ffffff !important" : undefined,
              borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
            }}
          >
            Cancelar
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            onClick={confirmDuplicate}
            disabled={duplicating || !duplicateNome.trim()}
            sx={{
              backgroundColor: darkMode ? "#007bff !important" : undefined,
              color: "#ffffff !important",
              fontWeight: "bold"
            }}
          >
            {duplicating ? <CircularProgress size={20} color="inherit" /> : "Duplicar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {openModal && (
        <CampanhaCreateEdit
          open={openModal}
          onClose={handleClose}
          id={selectedId}
        />
      )}
    </MDBox>
  );
}
